import React, {useCallback, useEffect} from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import navigateToURL from 'lib/navigateToURL';
import {
  AaMonomerByIdQuery,
  AaMonomerByIdQueryVariables,
} from 'molbio/aa-monomers/__generated/AaMonomerByIdQuery';
import {AA_MONOMER_BY_ID, AA_MONOMER_LIBRARY_URL} from 'molbio/aa-monomers/AaMonomerConstants';
import {updateAaMonomer, validateAaMonomerInput} from 'molbio/aa-monomers/AaMonomersDAO';
import {useCanCreateAaMonomersAndRedirect} from 'molbio/aa-monomers/admin/useCanCreateAaMonomers';
import SharedMonomerConfigurationPageContainer, {
  MONOMER_CONFIGURATION_PURPOSE,
} from 'molbio/monomers/admin/SharedMonomerConfigurationPageContainer';
import SharedMonomerManualEditPage from 'molbio/monomers/admin/SharedMonomerManualEditPage';
import {
  BaseMonomerUpdate,
  BaseMonomerValidationFields,
  SharedMonomerValidationOutput,
} from 'molbio/monomers/SharedMonomerConstants';
import notifications from 'notifications';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

export interface AaMonomerManualEditPageProps {
  aaMonomerId: string;
  // When true, the monomer is shown read-only with all inputs disabled and no save action. Used to view
  // natural monomers, which can never be edited.
  viewOnly?: boolean;
}

export default function AaMonomerManualEditPage({
  aaMonomerId,
  viewOnly = false,
}: AaMonomerManualEditPageProps): JSX.Element | null {
  // Editing an AA monomer requires the same permission as creating one; redirects with a toast otherwise.
  const {isLoading: isCheckingPermission} = useCanCreateAaMonomersAndRedirect({viewOnly});

  const queryResult = useBenchlingQuery<AaMonomerByIdQuery, AaMonomerByIdQueryVariables>(AA_MONOMER_BY_ID, {
    variables: {id: aaMonomerId},
    renderCachedDataWhenRefetching: true,
  });

  const aaMonomer = queryResult.type === QueryResultType.SUCCESS ? queryResult.data.aaMonomer : null;
  const monomer = aaMonomer?.__typename === 'AaMonomerNode' ? aaMonomer : null;
  // Missing, inaccessible, or natural monomers can't be edited, and a query error leaves us with nothing to
  // render, so in all of these cases we redirect back to the library. (useBenchlingQuery already surfaces a
  // notification for the error itself.)
  // Natural monomers can't be edited so they redirect too if not in view-only mode.
  const isEditingNaturalMonomer = !viewOnly && (monomer?.isNatural ?? false);
  const shouldRedirect =
    queryResult.type === QueryResultType.ERROR ||
    (queryResult.type === QueryResultType.SUCCESS && monomer == null) ||
    isEditingNaturalMonomer;

  const naturalAnalog = monomer?.naturalAnalog;
  const validate = useCallback(
    (inputs: Array<BaseMonomerValidationFields>): Promise<Array<SharedMonomerValidationOutput>> =>
      validateAaMonomerInput(inputs.map((input) => ({...input, naturalAnalogue: naturalAnalog}))),
    [naturalAnalog]
  );

  useEffect(() => {
    if (!shouldRedirect) {
      return;
    }
    if (isEditingNaturalMonomer) {
      notifications.info('Cannot edit a natural monomer');
    }
    navigateToURL(AA_MONOMER_LIBRARY_URL);
  }, [shouldRedirect, isEditingNaturalMonomer]);

  if (isCheckingPermission || queryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }

  if (monomer == null || shouldRedirect) {
    return null;
  }

  return (
    <SharedMonomerManualEditPage
      monomerId={aaMonomerId}
      initialName={monomer.name}
      initialCustomMolecularWeight={monomer.customMolecularWeight}
      initialSmilesInput={monomer.originalSmiles ?? monomer.canonicalSmiles}
      initialSymbol={monomer.symbol}
      validate={validate}
      onSave={async (update: BaseMonomerUpdate) => {
        await updateAaMonomer(aaMonomerId, update);
      }}
      libraryUrl={AA_MONOMER_LIBRARY_URL}
      pageContainer={
        <SharedMonomerConfigurationPageContainer
          purpose={viewOnly ? MONOMER_CONFIGURATION_PURPOSE.VIEW : MONOMER_CONFIGURATION_PURPOSE.EDIT}
          libraryName="AA monomer library"
          libraryUrl={AA_MONOMER_LIBRARY_URL}
        />
      }
      dataTestComponent="AaMonomerManualEditPage"
      readOnly={viewOnly}
    />
  );
}
